import { TaskResponse, TaskUiElementsResponse } from "@9amhealth/openapi";
import type { FC, ReactNode } from "react";
import React from "react";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import type { TaskResponseKnown } from "src/state/TaskManagementBloc/TaskManagementBloc";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugDivider,
  DebugInfoBox,
  DebugKeyValue
} from "./debug.styles";

export const createTimestamps = () => {
  const now = new Date().toISOString();
  return { createdAt: now, updatedAt: now };
};

export const getStatusBadgeVariant = (
  status?: string
): "success" | "danger" | "warning" | "info" => {
  switch (status) {
    case TaskResponse.status.COMPLETED:
      return "success";
    case TaskResponse.status.SKIPPED:
      return "warning";
    case TaskResponse.status.IN_PROGRESS:
    case TaskResponse.status.AVAILABLE:
      return "info";
    default:
      return "danger";
  }
};

export type TaskScenario<T extends string = string> = {
  id: T;
  name: string;
  description: string;
  tasks: TaskResponseKnown[];
};

export type TaskFactory = (
  status: TaskResponse.status,
  extraAdditionalData?: Record<string, unknown>
) => TaskResponseKnown;

export const createTaskFactory = (config: {
  id: string;
  slug: string;
  program: KnownProgram;
  group: string;
  title: string;
  iconType: TaskUiElementsResponse.iconType;
  popupType?: string;
  defaultBehavior?: TaskResponseKnown["behavior"];
  defaultAdditionalData?: Record<string, unknown>;
}): TaskFactory => {
  const {
    id,
    slug,
    program,
    group,
    title,
    iconType,
    popupType = "undefined",
    defaultBehavior = {
      userCanSee: true,
      userCanSkip: false,
      adminCanSkip: false,
      adminCanComplete: false
    },
    defaultAdditionalData = {}
  } = config;

  return (
    status: TaskResponse.status,
    extraAdditionalData: Record<string, unknown> = {}
  ): TaskResponseKnown => ({
    id,
    slug,
    program,
    group,
    status,
    ...createTimestamps(),
    additionalData: {
      skippable: defaultBehavior.userCanSkip,
      canBeSkippedByUser: defaultBehavior.userCanSkip,
      canBeSkippedByCareTeam: defaultBehavior.adminCanSkip,
      canBeCompletedByCareTeam: defaultBehavior.adminCanComplete,
      popupType,
      title,
      iconType,
      ...defaultAdditionalData,
      ...extraAdditionalData
    },
    uiElements: {
      popupType,
      title,
      iconType
    },
    behavior: defaultBehavior
  });
};

export const TaskMetadataDisplay: FC<{
  task: TaskResponseKnown | undefined;
  title: string;
  showFullDetails?: boolean;
}> = ({ task, title, showFullDetails = false }) => {
  const [expanded, setExpanded] = React.useState(showFullDetails);

  if (!task) {
    return (
      <DebugKeyValue>
        <span className="key">{title}</span>
        <span className="value">
          <DebugBadge variant="danger">Not Found</DebugBadge>
        </span>
      </DebugKeyValue>
    );
  }

  const hasAdditionalData =
    task.additionalData && Object.keys(task.additionalData).length > 0;

  return (
    <div style={{ marginBottom: "0.5rem" }}>
      <DebugKeyValue>
        <span className="key">{title}</span>
        <span
          className="value"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
        >
          <DebugBadge variant={getStatusBadgeVariant(task.status)}>
            {task.status}
          </DebugBadge>
          {hasAdditionalData && (
            <button
              onClick={() => setExpanded(!expanded)}
              style={{
                background: "none",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "2px 6px",
                fontSize: "10px",
                cursor: "pointer"
              }}
            >
              {expanded ? "▼ Hide" : "▶ Details"}
            </button>
          )}
        </span>
      </DebugKeyValue>

      {expanded && hasAdditionalData && (
        <div
          style={{
            marginLeft: "1rem",
            marginTop: "0.25rem",
            padding: "0.5rem",
            background: "#f8f8f8",
            borderRadius: "4px",
            fontSize: "11px"
          }}
        >
          <div style={{ marginBottom: "0.25rem", fontWeight: 600 }}>
            additionalData:
          </div>
          <pre
            style={{
              margin: 0,
              whiteSpace: "pre-wrap",
              fontSize: "10px",
              fontFamily: "monospace"
            }}
          >
            {JSON.stringify(task.additionalData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export type KeyFieldConfig = {
  label: string;
  getValue: (task: TaskResponseKnown) => string;
};

export const KeyFieldsDisplay: FC<{
  task: TaskResponseKnown | undefined;
  title: string;
  fields: KeyFieldConfig[];
  color?: string;
  bgColor?: string;
  borderColor?: string;
}> = ({
  task,
  title,
  fields,
  color = "#004085",
  bgColor = "#f0f7ff",
  borderColor = "#cce5ff"
}) => {
  if (!task) {
    return null;
  }

  return (
    <div
      style={{
        marginTop: "0.5rem",
        padding: "0.75rem",
        background: bgColor,
        borderRadius: "6px",
        border: `1px solid ${borderColor}`
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 600,
          marginBottom: "0.5rem",
          color
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: "11px", display: "grid", gap: "0.25rem" }}>
        {fields.map((field) => (
          <div key={field.label}>
            <span style={{ color: "#666" }}>{field.label}: </span>
            <code style={{ background: "#fff", padding: "1px 4px" }}>
              {field.getValue(task)}
            </code>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ConsolidatedTaskDisplay: FC<{
  consolidatedTask: TaskResponseKnown | undefined;
  statusLabel: string;
  statusValue: string | null;
  actionLabel?: string;
  actionValue?: string | null;
}> = ({
  consolidatedTask,
  statusLabel,
  statusValue,
  actionLabel,
  actionValue
}) => {
  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.75rem",
        background: "#f5f0ff",
        borderRadius: "6px",
        border: "1px solid #d4c5f9"
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 600,
          marginBottom: "0.5rem",
          color: "#5b21b6"
        }}
      >
        Consolidated Task:
      </div>
      <div style={{ fontSize: "11px", display: "grid", gap: "0.25rem" }}>
        <div>
          <span style={{ color: "#666" }}>{statusLabel}: </span>
          <DebugBadge
            variant={
              statusValue === "IN_PROGRESS"
                ? "info"
                : statusValue === "COMPLETED"
                  ? "success"
                  : statusValue === "SKIPPED"
                    ? "warning"
                    : "danger"
            }
          >
            {statusValue ?? "unknown"}
          </DebugBadge>
        </div>
        {actionLabel && (
          <div>
            <span style={{ color: "#666" }}>{actionLabel}: </span>
            <code style={{ background: "#fff", padding: "1px 4px" }}>
              {actionValue ?? "unknown"}
            </code>
          </div>
        )}
        <div>
          <span style={{ color: "#666" }}>consolidated task exists: </span>
          <DebugBadge variant={consolidatedTask ? "success" : "danger"}>
            {consolidatedTask ? "Yes" : "No"}
          </DebugBadge>
        </div>
      </div>

      {consolidatedTask && (
        <div
          style={{
            marginTop: "0.5rem",
            padding: "0.5rem",
            background: "#fff",
            borderRadius: "4px",
            fontSize: "10px"
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            Consolidated Task JSON:
          </div>
          <pre
            style={{
              margin: 0,
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              maxHeight: "150px",
              overflow: "auto"
            }}
          >
            {JSON.stringify(consolidatedTask, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export const ScenarioList: FC<{
  scenarios: TaskScenario[];
  activeScenario: string | null;
  onApply: (scenario: TaskScenario) => void;
}> = ({ scenarios, activeScenario, onApply }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {scenarios.map((scenario) => (
        <DebugButton
          key={scenario.id}
          className={activeScenario === scenario.id ? "primary" : undefined}
          onClick={() => onApply(scenario)}
          style={{ justifyContent: "flex-start", textAlign: "left" }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{scenario.name}</div>
            <div
              style={{ fontSize: "10px", opacity: 0.7, marginTop: "0.25rem" }}
            >
              {scenario.description}
            </div>
          </div>
        </DebugButton>
      ))}
    </div>
  );
};

export const DebugTaskScenarioPanel: FC<{
  title: string;
  description?: string;
  mockEnabled: boolean;
  onClearMock: () => void;
  children: ReactNode;
  scenarioSection: ReactNode;
}> = ({
  title,
  description,
  mockEnabled,
  onClearMock,
  children,
  scenarioSection
}) => {
  return (
    <div>
      <h4 style={{ fontSize: "13px", margin: "0 0 0.75rem", fontWeight: 600 }}>
        {title}
      </h4>

      <DebugKeyValue>
        <span className="key">Mock Enabled</span>
        <span className="value">
          <DebugBadge variant={mockEnabled ? "warning" : "info"}>
            {mockEnabled ? "Yes" : "No"}
          </DebugBadge>
        </span>
      </DebugKeyValue>

      {children}

      <DebugDivider />

      <h4 style={{ fontSize: "13px", margin: "0 0 0.75rem", fontWeight: 600 }}>
        Scenarios
      </h4>

      {description && (
        <DebugInfoBox style={{ marginBottom: "0.75rem" }}>
          {description}
        </DebugInfoBox>
      )}

      {mockEnabled && (
        <DebugButtonRow>
          <DebugButton className="danger" onClick={onClearMock}>
            Clear Mock & Reload
          </DebugButton>
        </DebugButtonRow>
      )}

      {scenarioSection}
    </div>
  );
};
